<template>
  <div
    ref="attachmentPreview"
    class="attachment-preview-wrap"
    tabindex="0"
    @click.stop=""
    @keyup="checkKey"
    @keydown.prevent.stop.esc="closePreview"
  >
    <div class="attachment-preview-modal" />
    <div class="attachment-preview-content">
      <AttachmentPreviewHeader
        :title="attachment.name"
        :is-pdf="checkIsPdf"
        @downloadAttachment="downloadAttachment"
        @closePreview="closePreview"
        @deleteAttachment="handleDelete"
        @copyLink="copyLink"
      />
      <div
        class="preview-content"
        :class="{ 'card-preview-content': isCard }"
      >
        <div
          class="attachment-navigation-button pt usn"
          :class="{ 'navigation-hidden': !showNavigationButtons }"
          @click.stop="previousAttachment"
        >
          <em
            :style="getCustomIconStyle(iconPath('chevron-down'))"
            class="custom-icons attachment-preview-icons previous-button usn"
          />
        </div>
        <div
          v-if="checkIsImage"
          ref="imageOuterWrap"
          class="image-outer-wrap"
        >
          <div
            ref="image"
            class="preview-image-parent"
          >
            <img
              class="preview-image usn"
              :class="{ 'bg-white': isSVG }"
              :src="attachmentUrl"
              alt=""
              @click.stop.prevent=""
            />
          </div>
        </div>
        <VideoAttachmentPreview
          v-if="checkIsVideo"
          :key="attachmentUrl"
          :attachment="attachment"
          :attachment-url="attachmentUrl"
          :loop="isOboardingActionsVideo"
          :force-autoplay="isOboardingActionsVideo"
          @exitFullScreen="handleFullScreenExit"
          @downloadAttachment="downloadAttachment"
          @closePreview="closePreview"
          @deleteAttachment="handleDelete"
          @copyLink="copyLink"
        />
        <div
          v-else-if="checkIsPdf"
          ref="pdfContainer"
          class="pdf-container"
        >
          <iframe
            ref="pdfObject"
            title="pdf"
            :src="attachmentUrl"
            type="application/pdf"
          ></iframe>
        </div>
        <div
          class="attachment-navigation-button pt"
          :class="{ 'navigation-hidden': !showNavigationButtons }"
          @click.stop="nextAttachment"
        >
          <em
            :style="getCustomIconStyle(iconPath('chevron-down'))"
            class="custom-icons attachment-preview-icons next-button usn"
          />
        </div>
      </div>
      <div
        v-if="checkIsImage"
        class="preview-footer"
      >
        <div class="footer-actions-wrap">
          <div class="image-footer-actions usn">
            <div
              class="attachment-preview-button-wrap"
              @click="zoomOut"
            >
              <em
                :style="getCustomIconStyle(iconPath('zoom-out'))"
                class="custom-icons attachment-preview-icons"
              />
            </div>
            <span class="preview-text zoom-value">{{ zoomValue }}%</span>
            <div
              class="attachment-preview-button-wrap"
              @click="zoomIn"
            >
              <em
                :style="getCustomIconStyle(iconPath('zoom-in'))"
                class="custom-icons attachment-preview-icons"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Panzoom from 'panzoom';
import { isImage, isVideo, isPdf } from '@/utilities/fileExtensions';
import VideoAttachmentPreview from '@/components/widgets/VideoAttachmentPreview';
import attachmentMixin from '@/mixins/attachmentMixin';
import AttachmentPreviewHeader from './AttachmentPreviewHeader';
import generateLink from '@/utilities/generateLink';
import { downloadFile } from '@/utilities/downloads';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import constants from '@/utilities/constants';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import { isPublicPageApp } from '@/utilities/isPublicPageApp';

export default {
  name: 'AttachmentPreview',
  components: {
    VideoAttachmentPreview,
    AttachmentPreviewHeader,
  },
  mixins: [attachmentMixin],
  provide() {
    return {
      showLinkButton:
        this.isCard && !this.attachmentOpenedFromInput && !this.isOboardingActionsVideo,
      showDeleteButton:
        this.isCard &&
        !this.attachmentOpenedFromInput &&
        !this.isOboardingActionsVideo &&
        !isPublicPageApp() &&
        !this.isGuestReader,
      showDownloadButton: !this.isOboardingActionsVideo && !this.checkIsPdf,
    };
  },

  setup() {
    const { isGuestReader } = useCurrentRole();
    const { iconPath, getCustomIconStyle } = useCustomIcon();

    return {
      isGuestReader,
      iconPath,
      getCustomIconStyle,
    };
  },

  data() {
    return {
      panzoom: null,
      x: null,
      y: null,
      zoomValue: 100,
    };
  },
  computed: {
    showNavigationButtons() {
      return this.attachmentsLength > 1;
    },
    isAnyCardDisplayed() {
      return this.$store.getters.getIsAnyCardDisplayed;
    },
    isPage() {
      if (isPublicPageApp()) return false;
      return this.$route.name === 'page' || this.$route.name === 'draftpage';
    },
    isCard() {
      return this.isAnyCardDisplayed;
    },
    currentCard() {
      return this.isCard ? this.$store.getters.getCardById(this.resourceId) : {};
    },
    currentPageID() {
      return this.$store.getters.getCurrentPageID;
    },
    resourceId() {
      if (this.isPage) {
        return this.currentPageID;
      }
      if (this.isCard) {
        return this.$store.getters.getPreviewAttachmentResourceId;
      }
      return '';
    },
    attachmentIndex() {
      return this.$store.getters.getPreviewAttachmentIndex;
    },
    attachmentsLength() {
      return this.$store.getters.getPreviewAttachments.length || 0;
    },
    attachment() {
      return this.$store.getters.getPreviewAttachments[this.attachmentIndex] || {};
    },
    checkIsImage() {
      return isImage(this.attachment.type);
    },
    checkIsVideo() {
      return isVideo(this.attachment.type);
    },
    checkIsPdf() {
      return isPdf(this.attachment.type);
    },
    attachmentUrl() {
      if (this.isOboardingActionsVideo || isPublicPageApp()) {
        return this.attachment.url;
      }
      const { id, local_id: localId, url } = this.attachment;
      if (id?.startsWith('unsplash-photo') || localId) {
        return url;
      }
      const baseRoute = this.$store.getters.getRoutes.files;
      const teamId = this.$store.getters.getTeamID;
      return `${baseRoute}/v1/${teamId}/files/${id}`;
    },
    attachmentOpenedFromInput() {
      return this.$store.getters.getAttachmentPreviewFromInput;
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
    isSVG() {
      return this.attachment.type === 'image/svg+xml';
    },
    isOboardingActionsVideo() {
      return this.attachment.id?.includes?.(constants.onboardingActionsVideoPrefix);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.focus();
      this.initializePanzoom();
      this.setDimensions();
    });
    window?.addEventListener('popstate', this.closePreview);
  },
  beforeUpdate() {
    this.$nextTick(() => {
      this.setDimensions();
      this.focus();
    });
  },
  unmounted() {
    if (this.panzoom) this.panzoom.dispose();
    window?.removeEventListener('popstate', this.closePreview);
  },
  methods: {
    closePreview() {
      this.$store.dispatch('clearCardPreviewData');
      this.$store.dispatch('setAttachmentPreviewFromInput', false);
      const route = new URL(window?.location.href);
      const attachmentId = route.searchParams.get('attachmentId');
      if (attachmentId) {
        route.searchParams.delete('attachmentId');
        window?.history.replaceState({ ...history.state, cardId: this.currentCard.id }, '', route);
      }
    },
    checkKey($event) {
      switch ($event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          this.nextAttachment();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          this.previousAttachment();
          break;
        default:
          break;
      }
    },

    nextAttachment() {
      this.$store.dispatch('setNextAttachment', this.resourceId);
      this.$nextTick(() => {
        this.initializePanzoom();
      });
      this.zoomValue = 100;
    },

    previousAttachment() {
      this.$store.dispatch('setPreviousAttachment', this.resourceId);
      this.$nextTick(() => {
        this.initializePanzoom();
      });
      this.zoomValue = 100;
    },

    focus() {
      this.$refs.attachmentPreview.focus();
    },
    zoomOut() {
      let newZoomValue = this.zoomValue - 25;
      if (this.zoomValue > 100) {
        newZoomValue = Math.max(newZoomValue, 100);
      }
      this.zoom(newZoomValue);
    },
    zoomIn() {
      let newZoomValue = this.zoomValue + 25;
      if (this.zoomValue < 100) {
        newZoomValue = Math.min(newZoomValue, 100);
      }
      this.zoom(newZoomValue);
    },
    zoom(zoomValue) {
      const zoomFactor = zoomValue / this.zoomValue;
      this.panzoom.smoothZoom(this.x, this.y, zoomFactor);
      this.zoomValue = zoomValue;
    },
    setDimensions() {
      const { imageOuterWrap } = this.$refs;
      if (imageOuterWrap) {
        this.x = imageOuterWrap.offsetWidth / 2;
        this.y = imageOuterWrap.offsetHeight / 2;
      }
    },
    initializePanzoom() {
      if (this.panzoom) this.panzoom.dispose();
      const { image } = this.$refs;
      if (image) {
        this.panzoom = Panzoom(image, {
          minZoom: 0.05,
          bounds: true,
          boundsPadding: 0.3,
          filterKey: () => true,
        });
        this.panzoom.on('zoom', (e) => {
          // fired while zooming in or out with the mousewheel or touchpad
          this.zoomValue = Math.round(e.getTransform().scale * 100);
        });
        this.panzoom.on('zoomend', (e) => {
          this.zoomValue = Math.round(e.getTransform().scale * 100);
        });
      }
    },
    handleFullScreenExit() {
      this.$nextTick(() => {
        this.focus();
      });
    },

    downloadAttachment() {
      downloadFile(this.attachment.url, this.attachment.name, this.attachment.type);
    },

    handleDelete() {
      const { id } = this.attachment;
      if (!id) return;
      this.$store.dispatch('deleteAttachmentFromPreviewAttachments', this.attachment.id);
      this.deleteAttachment(this.currentCard.id, id);
      if (this.attachmentsLength > 0) {
        this.$store.dispatch('setPreviousAttachment');
      } else {
        this.closePreview();
      }
    },

    copyLink() {
      const link = `${generateLink(
        'card',
        this.teamSlug,
        this.currentCard.id,
        this.currentCard.title
      )}?attachmentId=${this.attachment.id}`;
      navigator.clipboard.writeText(link);

      this.$store.dispatch('addUiNotification', {
        message: this.translate('linkCopiedToClipboard'),
        icon: 'link-st',
        duration: 4000,
      });
    },
  },
};
</script>

<style lang="scss">
.attachment-preview-wrap {
  height: 100vh;
  width: 100vw;
  position: absolute;
  top: 0;
  z-index: 1000;
  outline: none;

  .attachment-preview-modal {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    background: rgba($gray900, 0.9);
    z-index: -1;
  }

  .attachment-preview-content {
    height: 100vh;
    width: 100vw;

    .attachment-name {
      margin-left: 24px;
      font-size: 16px;
      line-height: 24px;
      display: flex;

      span {
        display: flex;
        white-space: break-spaces;
        word-break: break-all;
      }
    }

    .preview-text {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      min-width: 34px;
      text-align: center;
    }

    .preview-content {
      display: flex;
      justify-content: center;
      align-items: center;
      height: calc(100% - (2 * #{$headerFooterHeight}));
      outline: none;
      padding-top: $headerFooterHeight;

      &.card-preview-content {
        justify-content: space-between;
      }

      .image-outer-wrap {
        width: 100%;
        height: 100%;
        outline: none;

        .preview-image-parent {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          cursor: move;

          .preview-image {
            max-width: 100%;
            max-height: 100%;
            z-index: 0;
            border-radius: 4px;
            overflow: hidden;
          }
        }
      }

      .attachment-navigation-button {
        width: 48px;
        min-width: 48px;
        height: 48px;
        background: transparent;
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0 24px;
        z-index: 1;
        opacity: 1;
        transition: opacity 0.3s ease-in-out;

        &.navigation-hidden {
          opacity: 0;
        }

        &:hover:not(.mobile *) {
          background: $gray700;
        }
      }

      .pdf-container {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: auto;

        iframe {
          width: 100%;
          height: 100%;
          max-width: 100%;
          border-radius: 8px;
          border: none;
        }
      }
    }

    .preview-footer {
      height: $headerFooterHeight;
      color: #ffffff;
      z-index: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      transform: translateZ(0);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 4.94%, rgba(0, 0, 0, 0.595) 64.66%);

      .attachment-preview-button-wrap {
        @apply rounded flex items-center justify-center w-7 h-7 cursor-pointer select-none;
      }

      .footer-actions-wrap,
      .image-footer-actions {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;

        .zoom-value {
          font-weight: 500;
          margin: 0 16px;
        }
      }
      .image-footer-actions {
        > div {
          &:hover:not(.mobile *) {
            background: $gray700;
          }
        }
      }
    }
  }
}

// mobile style

.mobile {
  .attachment-preview-wrap {
    .attachment-preview-content {
      .preview-content {
        .attachment-navigation-button {
          opacity: 1;
          position: absolute;
          background-color: rgba($gray700, 0.3);

          &:first-child {
            left: 0;
          }
          &:last-child {
            right: 0;
          }
        }
      }
    }
  }
}
</style>
